import React, { useEffect, useState } from "react";
import { ArrowLeft, Truck, Check, Package, Clock, CheckCircle2, XCircle, MapPin, DollarSign, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STAGES = [
    { key: "order_placed", label: "Order Placed", icon: Clock },
    { key: "processing", label: "Processing", icon: Package },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
    { key: "delivered", label: "Delivered", icon: CheckCircle2 },
    { key: "canceled", label: "Canceled", icon: XCircle },
];

export const AdminOrderDetail = ({ reference, token, onBack }) => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState("");
    const [trackingCarrier, setTrackingCarrier] = useState("");
    const [note, setNote] = useState("");

    const headers = { "X-Admin-Token": token };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/orders/${reference}`, { headers });
            setOrder(data);
            setTrackingNumber(data.tracking_number || "");
            setTrackingCarrier(data.tracking_carrier || "");
        } catch {
            toast.error("Failed to load order");
        }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [reference]);

    const updateStatus = async (status) => {
        if (!window.confirm(`Update order to "${STAGES.find(s => s.key === status)?.label}"? This will email the customer.`)) return;
        setUpdating(true);
        try {
            const payload = { status };
            if (status === "shipped" || status === "out_for_delivery") {
                if (trackingNumber.trim()) payload.tracking_number = trackingNumber.trim();
                if (trackingCarrier.trim()) payload.tracking_carrier = trackingCarrier.trim();
            }
            if (note.trim()) payload.note = note.trim();
            const { data } = await api.patch(`/admin/orders/${reference}/status`, payload, { headers });
            setOrder(data);
            setNote("");
            toast.success(`Order moved to ${STAGES.find(s => s.key === status)?.label}. Email sent.`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Status update failed");
        }
        setUpdating(false);
    };

    const markPaid = async () => {
        if (!window.confirm(`Mark order ${reference} as paid? This releases it to dispatch and emails the customer.`)) return;
        setUpdating(true);
        try {
            await api.post(`/admin/orders/${reference}/mark-paid`, {}, { headers });
            toast.success("Order marked paid");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Action failed");
        }
        setUpdating(false);
    };

    const refundToCredit = async () => {
        const defaultAmt = Number(order?.amount || 0).toFixed(2);
        const raw = window.prompt(`Refund as STORE CREDIT to this customer.\n\nThis adds wallet balance — it does NOT push money back to PayPal/PayID. Use this for goodwill or when a customer wants credit instead of cash.\n\nAmount (max A$${defaultAmt}):`, defaultAmt);
        if (!raw) return;
        const amount = parseFloat(raw);
        if (!amount || amount <= 0) return toast.error("Invalid amount");
        const reason = window.prompt("Reason / note (visible to customer in email):", "Goodwill refund");
        if (reason === null) return;
        setUpdating(true);
        try {
            const { data } = await api.post(`/admin/orders/${reference}/refund-to-credit`,
                { amount, reason: reason.trim() || "Refund" }, { headers });
            toast.success(`A$${Number(amount).toFixed(2)} added to wallet. New balance: A$${Number(data.balance).toFixed(2)}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refund failed");
        }
        setUpdating(false);
    };

    if (loading) {
        return (
            <div className="py-16 text-center text-[var(--text-secondary)]">Loading order…</div>
        );
    }
    if (!order) return null;

    const a = order.shipping_address || {};
    const currentStatus = order.fulfillment_status;
    const isPaid = order.payment_status === "paid";

    return (
        <div data-testid={`admin-order-detail-${reference}`}>
            <button
                onClick={onBack}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--gold)] mb-5"
                data-testid="admin-order-back"
            >
                <ArrowLeft size={14} /> Back to orders
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Order</div>
                    <h2 className="font-display text-3xl font-extrabold text-white tracking-tighter italic">
                        <span className="text-[var(--gold)]">#{order.reference}</span>
                    </h2>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">
                        {new Date(order.created_at).toLocaleString("en-AU")} · {order.provider}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-3xl font-display font-extrabold text-white">A${Number(order.amount || 0).toFixed(2)}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{(order.currency || "aud").toUpperCase()}</div>
                </div>
            </div>

            {/* Customer + shipping */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="glass-elevated rounded-2xl p-5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">Customer</div>
                    <div className="text-white font-semibold">{order.customer_name || "—"}</div>
                    <div className="text-sm text-[var(--text-secondary)] mt-1">{order.customer_email || "—"}</div>
                    <div className="mt-3 text-xs text-[var(--text-muted)] font-mono">
                        Payment: <span className={isPaid ? "text-emerald-300" : "text-amber-300"}>{order.payment_status}</span>
                    </div>
                </div>
                <div className="glass-elevated rounded-2xl p-5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2 inline-flex items-center gap-2">
                        <MapPin size={11} /> Shipping
                    </div>
                    {a.line1 ? (
                        <div className="text-sm text-white leading-relaxed">
                            <div className="font-semibold">{a.full_name}</div>
                            <div className="text-[var(--text-secondary)]">
                                {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                                {a.suburb} {a.state} {a.postcode}<br />
                                {a.country}{a.phone ? ` · ${a.phone}` : ""}
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-[var(--text-muted)] italic">No shipping address captured.</div>
                    )}
                    {/* Shipping method chosen by the customer at checkout */}
                    <div className="mt-3 pt-3 border-t border-white/5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Shipping method</div>
                        <div className="text-sm text-white font-mono" data-testid="admin-shipping-method">
                            {order.shipping_method_display
                                || order.shipping_method_name
                                || order.shipping_method_code
                                || "—"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Pre-order + mixed-shipment info */}
            {(order.has_pre_order || order.mixed_order) && (
                <div className="glass-elevated rounded-2xl p-5 mb-6 border border-[var(--gold)]/40 bg-[var(--gold)]/5" data-testid="admin-pre-order-block">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">
                        {order.mixed_order ? "Mixed order (in-stock + pre-order)" : "Pre-order in this order"}
                    </div>
                    {(order.pre_order_items || []).length > 0 && (
                        <div className="space-y-1.5 text-sm">
                            {order.pre_order_items.map((it, i) => (
                                <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-1 border-b border-white/5 last:border-0">
                                    <span className="text-white font-mono">
                                        {it.name} <span className="text-[var(--text-muted)]">×{it.quantity}</span>
                                    </span>
                                    <span className="text-[var(--text-secondary)] text-xs">
                                        {it.expected_arrival_date && <>Arrives: <span className="text-white">{it.expected_arrival_date}</span></>}
                                        {it.estimated_dispatch_date && <>{it.expected_arrival_date ? " · " : ""}Dispatch: <span className="text-white">{it.estimated_dispatch_date}</span></>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {order.mixed_order && (
                        <div className="mt-3 pt-3 border-t border-white/10 text-sm">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Customer chose</div>
                            <div className="text-white font-mono" data-testid="admin-mixed-choice">
                                {order.mixed_order_choice === "ship_separately"
                                    ? "Ship available items now, pre-order later"
                                    : "Ship everything together when pre-order arrives"}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Items */}
            <div className="glass-elevated rounded-2xl p-5 mb-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-3">Items</div>
                <div className="space-y-1.5">
                    {(order.items || []).map((it, i) => (
                        <div key={i} className="flex justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                            <span className="text-white font-mono">{it.product_slug}</span>
                            <span className="text-[var(--text-secondary)] font-mono">×{it.quantity}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Mark paid CTA if unpaid */}
            {!isPaid && (order.payment_status === "awaiting_payment") && (
                <div className="glass-elevated rounded-2xl p-5 mb-6 border border-amber-500/30 bg-amber-500/5">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-amber-200 font-display font-bold">Awaiting payment</div>
                            <div className="text-xs text-amber-200/60">Mark as paid once the transfer clears.</div>
                        </div>
                        <button
                            onClick={markPaid}
                            disabled={updating}
                            className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 text-xs font-mono uppercase tracking-widest hover:bg-emerald-500/30 inline-flex items-center gap-2"
                            data-testid={`admin-order-mark-paid-${reference}`}
                        >
                            <DollarSign size={12} /> Mark paid
                        </button>
                    </div>
                </div>
            )}

            {/* Status workflow */}
            {isPaid && (
                <div className="glass-elevated rounded-2xl p-5 mb-6" data-testid="admin-order-status-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)]">Fulfillment status</div>
                        {!order.refunded_to_credit && (
                            <button
                                onClick={refundToCredit}
                                disabled={updating}
                                className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-purple-500/40 text-purple-200 bg-purple-500/10 hover:bg-purple-500/20 inline-flex items-center gap-1.5 disabled:opacity-40"
                                data-testid="admin-refund-to-credit"
                            >
                                <Wallet size={12} /> Refund as store credit
                            </button>
                        )}
                        {order.refunded_to_credit && (
                            <div className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-purple-500/40 text-purple-200 bg-purple-500/10 inline-flex items-center gap-1.5">
                                <Wallet size={12} /> A${Number(order.refunded_to_credit).toFixed(2)} refunded to wallet
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                        {STAGES.map((s) => {
                            const active = s.key === currentStatus;
                            const Icon = s.icon;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => updateStatus(s.key)}
                                    disabled={updating || active}
                                    className={`px-3 py-2.5 rounded-xl border text-left text-xs font-mono uppercase tracking-wider transition-colors inline-flex items-center gap-2 ${
                                        active
                                            ? "border-[var(--gold)] bg-[rgba(233,188,11,0.1)] text-[var(--gold)] cursor-default"
                                            : "border-white/10 text-[var(--text-secondary)] hover:border-[var(--gold)]/40 hover:text-white"
                                    }`}
                                    data-testid={`admin-status-btn-${s.key}`}
                                >
                                    <Icon size={12} /> {s.label}
                                    {active && <Check size={12} className="ml-auto" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tracking + note */}
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                        <input
                            type="text"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            placeholder="Tracking number (optional)"
                            className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                            data-testid="admin-tracking-number"
                        />
                        <input
                            type="text"
                            value={trackingCarrier}
                            onChange={(e) => setTrackingCarrier(e.target.value)}
                            placeholder="Carrier (e.g. Auspost, StarTrack)"
                            className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                            data-testid="admin-tracking-carrier"
                        />
                    </div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note for the customer (included in email)"
                        rows={2}
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] resize-none"
                        data-testid="admin-status-note"
                    />
                    <p className="text-[10px] text-[var(--text-muted)] mt-2">
                        Tracking & note are saved when you click a status above. Customer is emailed on every status change.
                    </p>
                </div>
            )}

            {/* Status history */}
            {order.status_history && order.status_history.length > 0 && (
                <div className="glass rounded-2xl p-5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-3">History</div>
                    <div className="space-y-2">
                        {[...order.status_history].reverse().map((h, i) => (
                            <div key={`${h.status || "step"}-${h.changed_at || i}`} className="flex items-start gap-3 text-sm pb-2 border-b border-white/5 last:border-0">
                                <Check size={12} className="text-[var(--gold)] mt-1 shrink-0" />
                                <div className="flex-1">
                                    <div className="text-white font-mono">{h.label || h.status}</div>
                                    {h.note && <div className="text-[var(--text-secondary)] text-xs mt-0.5">{h.note}</div>}
                                    {h.tracking_number && (
                                        <div className="text-[var(--text-muted)] text-xs mt-0.5 font-mono">
                                            Tracking: {h.tracking_number}{h.tracking_carrier ? ` · ${h.tracking_carrier}` : ""}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10px] text-[var(--text-muted)] font-mono shrink-0">
                                    {new Date(h.changed_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
